import React from 'react';
import { FlexColumn, FlexRow, Text, FilledButton, SvgIcon } from '@make-software/tg-mini-app-ui';

import { BaseModal, ModalContentWrapper } from '@/shared';

import {
  ErrorText,
  ProviderButtonsCol,
  ProviderIcon,
  StyledButtonCaption,
  StyledHeader,
  ProviderButtonContent,
  modalStyles
} from './auth-provider-modal.styled';

export interface AuthProvider {
  id: string;
  name: string;
  icon: string;
  redirectUrl: string;
  disabled?: boolean;
}

interface AuthProviderModalProps {
  isOpen: boolean;
  providers: AuthProvider[];
  selectedProvider: string | null;
  onProviderSelect: (providerId: string) => void;
  error?: string | null;
  onErrorRetry?: () => void;
  friendID?: string | null;
  logoSrc?: string;
}

export const AuthProviderModal: React.FC<AuthProviderModalProps> = ({
  isOpen,
  logoSrc,
  providers,
  onProviderSelect,
  error,
  onErrorRetry
}) => {
  // Show error modal if there's an error
  if (error) {
    return (
      <BaseModal
        isOpen={isOpen}
        onRequestClose={() => {}}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        style={modalStyles}>
        <ModalContentWrapper direction="column" justify="center" style={{ minHeight: '200px' }}>
          <FlexColumn gap={24}>
            <FlexRow align="center" justify="center" gap={8}>
              <Text weight={2}>Authentication Error</Text>
            </FlexRow>

            <FlexRow align="center" justify="center">
              <Text weight={3}>
                <ErrorText>{error}</ErrorText>
              </Text>
            </FlexRow>

            {onErrorRetry && (
              <FlexRow gap={16} justify="center">
                <FilledButton title="Try Again" onClick={onErrorRetry} />
              </FlexRow>
            )}
          </FlexColumn>
        </ModalContentWrapper>
      </BaseModal>
    );
  }

  // Show provider selection modal
  return (
    <BaseModal
      isOpen={isOpen}
      onRequestClose={() => {}}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      style={modalStyles}>
      <ModalContentWrapper
        direction="column"
        justify="center"
        align="center"
        style={{ minHeight: '200px' }}>
        <FlexColumn gap={24} align="center" style={{ minWidth: '300px' }}>
          {logoSrc && <SvgIcon src={logoSrc} width="200px" height="70px" style={{ marginBottom: 32 }} />}

          <FlexRow align="center" justify="center" gap={8}>
            {providers.length > 0
              ? <StyledHeader weight={2}>{ providers.length > 1
                ? 'Choose your auth provider'
                : `Click on ${providers[0].name} to continue`}</StyledHeader>
              : <StyledHeader weight={2} style={{textAlign: 'center'}}>The next CSPR.fans season is almost here.<p/>
                Stay tuned!</StyledHeader>
            }
          </FlexRow>

          <ProviderButtonsCol>
            {providers.map((provider) => (
              <FlexRow key={provider.name} style={{ width: '100%' }}>
                <FilledButton
                  key={provider.id}
                  title={
                    <ProviderButtonContent gap={8} align="center">
                      <ProviderIcon
                        src={provider.icon}
                        alt={provider.name}
                        width={24}
                        height={24}
                      />
                      <StyledButtonCaption>{provider.name}</StyledButtonCaption>
                    </ProviderButtonContent>
                  }
                  onClick={() => onProviderSelect(provider.id)}
                  borderRadius="0"
                  disabled={provider.disabled === true}
                />
              </FlexRow>
            ))}
          </ProviderButtonsCol>
        </FlexColumn>
      </ModalContentWrapper>
    </BaseModal>
  );
};
